import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import {
  motion,
  AnimatePresence,
  Variants,
} from "framer-motion";
import { useInView } from "react-intersection-observer";
import { useNavigate } from "react-router-dom";
import { cases } from "../../data/cases/cases";

// Individual Case Component
const CaseItem: React.FC<{
  caseItem: any;
  index: number;
  onMouseMove: (
    e: React.MouseEvent<HTMLDivElement>,
    caseId: number
  ) => void;
  onMouseLeave: () => void;
  hoveredCase: number | null;
  mousePosition: { x: number; y: number };
}> = ({
  caseItem,
  index,
  onMouseMove,
  onMouseLeave,
  hoveredCase,
  mousePosition,
}) => {
  const navigate = useNavigate();

  const handleCaseClick = () => {
    const caseName = caseItem.name
      .toLowerCase()
      .replace(/\s+/g, "-");
    navigate(`/cases/${caseName}`);
  };
  const [caseRef, caseInView] = useInView({
    triggerOnce: false,
    threshold: 0.3,
    rootMargin: "-50px 0px -50px 0px",
  });

  return (
    <motion.div
      ref={caseRef}
      initial="hidden"
      animate={caseInView ? "visible" : "exit"}
      variants={zoomInOut}
      transition={{ delay: index * 0.1 }}
      className="group relative aspect-[3/4]"
    >
      {/* Small Rectangle Container */}
      <div
        className="relative w-full h-full overflow-hidden cursor-pointer bg-transparent border-[0.1px] border-gray-300 dark:border-gray-900"
        onMouseMove={(e) => onMouseMove(e, caseItem.id)}
        onMouseLeave={onMouseLeave}
        onClick={handleCaseClick}
      >
        {/* Phone Mockup - Filled to full case */}
        <div className="absolute inset-0 flex items-center justify-center p-6">
          <div className="relative w-full h-full rounded-xl shadow-xl overflow-hidden">
            <img
              src={caseItem.image}
              alt={caseItem.name}
              className="w-full h-full object-cover"
            />
          </div>
        </div>

        {/* Hover Overlay - Information paragraph following mouse */}
        <AnimatePresence>
          {hoveredCase === caseItem.id && (
            <motion.div
              initial={{
                opacity: 0,
                scale: 0.8,
              }}
              animate={{
                opacity: 1,
                scale: 1,
              }}
              exit={{
                opacity: 0,
                scale: 0.8,
              }}
              transition={{
                duration: 0.2,
                ease: "easeOut",
              }}
              className="absolute pointer-events-none z-10"
              style={{
                left: mousePosition.x + 10,
                top: mousePosition.y - 40,
              }}
            >
              <div className="bg-white dark:bg-gray-800 rounded-lg p-3 shadow-xl">
                <div className="text-center">
                  <h3
                    className="text-sm font-bold mb-1 text-gray-900 dark:text-white"
                    style={{
                      fontFamily: "Merriweather, serif",
                    }}
                  >
                    {caseItem.name}
                  </h3>
                  <p
                    className="text-xs italic text-gray-600 dark:text-gray-300"
                    style={{
                      fontFamily: "Poppins, sans-serif",
                    }}
                  >
                    {caseItem.year}
                  </p>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
};

// Animation variants
const fadeInUp: Variants = {
  hidden: { opacity: 0, y: 60 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: "easeOut",
    },
  },
};

// Zoom animation variants
const zoomInOut: Variants = {
  hidden: {
    opacity: 0,
    scale: 0.8,
    transition: {
      duration: 0.4,
      ease: "easeOut",
    },
  },
  visible: {
    opacity: 1,
    scale: 1,
    transition: {
      duration: 0.6,
      ease: "easeOut",
    },
  },
  exit: {
    opacity: 0,
    scale: 0.8,
    transition: {
      duration: 0.4,
      ease: "easeIn",
    },
  },
};

const Cases: React.FC = () => {
  const [hoveredCase, setHoveredCase] = useState<
    number | null
  >(null);
  const [mousePosition, setMousePosition] = useState({
    x: 0,
    y: 0,
  });
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const handleMouseMove = (
    e: React.MouseEvent<HTMLDivElement>,
    caseId: number
  ) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    setMousePosition({ x, y });
    setHoveredCase(caseId);
  };

  return (
    <>
      <Helmet>
        <title>
          Our Cases - VEA Group Portfolio & Projects
        </title>
        <meta
          name="description"
          content="Explore VEA Group's successful portfolio of digital transformation projects, e-commerce platforms, mobile applications, and digital marketing campaigns."
        />
        <meta
          name="keywords"
          content="cases, portfolio, digital transformation, e-commerce, mobile apps, digital marketing, VEA Group, projects"
        />
        <link
          rel="canonical"
          href="https://veagroup.global/cases"
        />
        <link rel="icon" href="/favicon.ico" />
        <link rel="manifest" href="/manifest.json" />
        {/* Open Graph */}
        <meta
          property="og:title"
          content="Our Cases - VEA Group Portfolio & Projects"
        />
        <meta
          property="og:description"
          content="Explore VEA Group's successful portfolio of digital transformation projects, e-commerce platforms, mobile applications, and digital marketing campaigns."
        />
        <meta property="og:type" content="website" />
        <meta
          property="og:url"
          content="https://veagroup.global/cases"
        />
        <meta property="og:image" content="/logo.png" />
        {/* Twitter Card */}
        <meta
          name="twitter:card"
          content="summary_large_image"
        />
        <meta
          name="twitter:title"
          content="Our Cases - VEA Group Portfolio & Projects"
        />
        <meta
          name="twitter:description"
          content="Explore VEA Group's successful portfolio of digital transformation projects, e-commerce platforms, mobile applications, and digital marketing campaigns."
        />
        <meta name="twitter:image" content="/logo.png" />
      </Helmet>

      <header
        className="header relative w-full bg-cover bg-center text-center"
        style={{
          backgroundImage:
            "url('/images/case_background.jpg')",
          minHeight: "400px",
        }}
      >
        <div className="absolute inset-0 bg-black bg-opacity-50"></div>
        <div className="absolute z-10 top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-center">
          <h1 className="text-5xl font-bold text-white">
            Cases
          </h1>
        </div>
      </header>

      <section className="min-h-screen bg-white dark:bg-black transition-colors duration-500">
        <div className="container mx-auto px-4 py-16 md:py-32">
          {/* Cases Grid - Large Frame with Thin Borders */}
          <motion.div
            ref={ref}
            initial="hidden"
            animate="visible"
            variants={fadeInUp}
            className="max-w-6xl mx-auto"
          >
            {/* Large Rectangular Frame with Thin Borders */}
            <div className="relative bg-transparent dark:border-gray-900 rounded-none">
              <div
                className={`grid grid-cols-2 dark:divide-gray-900`}
                style={{
                  gridTemplateRows: `repeat(${Math.ceil(
                    cases.length / 2
                  )}, minmax(0, 1fr))`,
                }}
              >
                {cases.map((caseItem, index) => (
                  <CaseItem
                    key={caseItem.id}
                    caseItem={caseItem}
                    index={index}
                    onMouseMove={handleMouseMove}
                    onMouseLeave={() =>
                      setHoveredCase(null)
                    }
                    hoveredCase={hoveredCase}
                    mousePosition={mousePosition}
                  />
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default Cases;
